import { useCallback } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop';
import {
	ContentAnalyticsViewedComments,
	type ContentAnalyticsViewedCommentsType,
} from '@confluence/inline-comments-queries';
import gql from 'graphql-tag';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { containsNoNetworkError } from '@confluence/network';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

export interface UseViewedCommentsResult {
	getViewedCommentsCacheOnly: () => {
		success: boolean;
		data?: Set<string>;
		error?: Error;
	};
	getViewedCommentsFresh: () => Promise<{ success: boolean; data?: Set<string>; error?: Error }>;
	updateViewedCommentsCache: (commentIds: string[]) => void;
}

// Type for the ViewedComments fragment data
interface ViewedCommentsFragmentData {
	commentIds: string[];
	__typename: 'ViewedComments';
}

export const useViewedComments = ({ pageId }: { pageId: string }): UseViewedCommentsResult => {
	const writeFragmentInterop = useWriteFragmentInterop<ViewedCommentsFragmentData>({
		fragmentSpec: gql`
			fragment ViewedCommentsFragment on ViewedComments {
				commentIds
			}
		`,
	});

	const readFragmentInterop = useReadFragmentInterop<ViewedCommentsFragmentData>({
		fragmentSpec: gql`
			fragment ViewedCommentsFragment on ViewedComments {
				commentIds
			}
		`,
	});

	// Network-only query for fresh data
	const loadViewedCommentsNetworkOnlyPromise = useQueryInterop<ContentAnalyticsViewedCommentsType>(
		ContentAnalyticsViewedComments,
		{
			fetchPolicy: 'network-only',
			variables: { contentId: pageId },
		},
	);

	// Helper function to process the query result
	const processQueryResult = useCallback((data: ContentAnalyticsViewedCommentsType) => {
		const commentIds = data?.contentAnalyticsViewedComments?.commentIds ?? [];
		const viewedCommentsSet = new Set(commentIds.filter((id): id is string => id !== null));

		return {
			success: true,
			data: viewedCommentsSet,
		};
	}, []);

	// Helper function for cache-only queries
	const executeCacheOnlyQuery = useCallback(() => {
		if (!pageId) {
			throw new Error('Page ID is required');
		}

		try {
			// Use readFragmentInterop to get fresh data from cache
			const fragmentId = `ViewedComments:${pageId}`;
			const data = readFragmentInterop({ id: fragmentId });

			if (data) {
				// Convert fragment data to query result format
				const queryResult = {
					contentAnalyticsViewedComments: {
						commentIds: data.commentIds,
						__typename: 'ViewedComments',
					},
				};
				return processQueryResult(queryResult);
			} else {
				return {
					success: false,
					error: new Error('No viewed comments data found in cache'),
				};
			}
		} catch (error) {
			// Handle cache read errors - these are local operations so we just log unexpected errors
			setContentErrorAttributes(error, {
				objectId: pageId,
				objectType: 'page',
			});

			getMonitoringClient().submitError(error, {
				attribution: Attribution.COMMENTS,
			});

			markErrorAsHandled(error);
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [pageId, processQueryResult, readFragmentInterop]);

	// Helper function for network-only queries
	const executeNetworkOnlyQuery = useCallback(
		async (
			refetchFn: (variables?: any) => Promise<{ data: ContentAnalyticsViewedCommentsType }>,
		) => {
			if (!pageId) {
				throw new Error('Page ID is required');
			}

			try {
				const result = await refetchFn({ contentId: pageId });
				return processQueryResult(result.data);
			} catch (error) {
				// Handle expected errors (403/unauthorized, network errors) - just mark as handled
				if (isUnauthorizedError(error) || containsNoNetworkError(error)) {
					markErrorAsHandled(error);
					return {
						success: false,
						error: error as Error,
					};
				}

				// Handle unexpected errors (including 500) - track them with context
				setContentErrorAttributes(error, {
					objectId: pageId,
					objectType: 'page',
				});

				getMonitoringClient().submitError(error, {
					attribution: Attribution.COMMENTS,
				});

				markErrorAsHandled(error);
				return {
					success: false,
					error: error as Error,
				};
			}
		},
		[pageId, processQueryResult],
	);

	// Cached data query - uses cache-only for performance
	const getViewedCommentsCacheOnly = useCallback(() => {
		try {
			return executeCacheOnlyQuery();
		} catch (error) {
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [executeCacheOnlyQuery]);

	// Fresh data query - always uses network-only for latest data
	const getViewedCommentsFresh = useCallback(
		() => executeNetworkOnlyQuery(loadViewedCommentsNetworkOnlyPromise.refetch),
		[executeNetworkOnlyQuery, loadViewedCommentsNetworkOnlyPromise],
	);

	// Function to update the viewed comments cache
	const updateViewedCommentsCache = useCallback(
		(commentIds: string[]) => {
			if (!pageId || commentIds.length === 0) {
				return;
			}

			try {
				// Read existing cache data using readFragmentInterop
				const fragmentId = `ViewedComments:${pageId}`;
				const cacheData = readFragmentInterop({ id: fragmentId });

				if (!cacheData) {
					// Cache doesn't exist yet, create new cache entry
					writeFragmentInterop({
						fragmentId,
						data: {
							commentIds: commentIds,
							__typename: 'ViewedComments',
						},
					});
					return;
				}

				// Get existing comment IDs from cache
				const existingCommentIds = cacheData?.commentIds ?? [];
				const existingSet = new Set(existingCommentIds.filter((id): id is string => id !== null));

				// Add new comment IDs to the set
				commentIds.forEach((id) => existingSet.add(id));

				// Update the cache with the new data
				writeFragmentInterop({
					fragmentId,
					data: {
						commentIds: Array.from(existingSet),
						__typename: 'ViewedComments',
					},
				});
			} catch (error) {
				// Handle cache writing errors - these are local operations so we just log unexpected errors
				setContentErrorAttributes(error, {
					objectId: pageId,
					objectType: 'page',
				});

				getMonitoringClient().submitError(error, {
					attribution: Attribution.COMMENTS,
				});

				markErrorAsHandled(error);
			}
		},
		[pageId, readFragmentInterop, writeFragmentInterop],
	);

	return {
		getViewedCommentsCacheOnly,
		getViewedCommentsFresh,
		updateViewedCommentsCache,
	};
};
